/**
 * CANONICAL MODULE — app-platform/shared/themeGround.ts
 *
 * Copied byte-for-byte into every app that takes the theme engine (`src/lib/themeGround.ts`). Pure;
 * imports only its sibling `themeColour.ts`. **Fix bugs HERE, then sync outward.**
 *
 * ── What it is ───────────────────────────────────────────────────────────────────────────────────
 * The ground-ladder solver (design/W-theme-contract.md §6 A): one base colour and a scheme become
 * `bg`, `surface-1`, `surface-2`, `sunk`, plus app extras. Two recipes, both config:
 *
 *   - `steps`: the base IS `bg`, literally; the other steps are fixed HSL lightness/saturation
 *     deltas per scheme. What a pasted theme code uses (its `bg` is an input, never altered).
 *   - `quil-hue`: quil's `resolveGround` (`src/lib/ground.ts`) generalised, every constant moved into
 *     the recipe: pinned literals in light (the pick returns as ink and hairline); hue on black in
 *     oled; in dark, lightness solved so a fixed ink reaches its target (capped), then darkened until a
 *     fixed lit colour separates from the checked steps. `QUIL_HUE_RECIPE` holds quil's values today
 *     and reproduces its outputs exactly (themeGolden.test.ts); at adoption it moves into quil's
 *     profile (`profiles/quil.json`), and this module keeps no app's numbers.
 *     One deliberate departure from quil's old solver (2026-11-01): an ACHROMATIC pick (a grey,
 *     black or white, see `ACHROMATIC_MAX_SPREAD`) gives a neutral ladder in every scheme instead
 *     of the hue-0 red-brown the old one gave every grey. Every chromatic pick is unchanged.
 */

import { contrast, hslToRgb, mustParse, rgbToHsl, toHex } from './themeColour'
import type { RGB, SolveStep } from './themeColour'

export type Scheme = 'light' | 'dark' | 'oled'

interface GroundInput {
  base: string
  scheme: Scheme
}

interface GroundResult {
  bg: string
  surface1: string
  surface2: string
  sunk: string
  /** App-private outputs: `topbar`, a tinted `text`, a `line` (quil). Keys are free per recipe. */
  extras: Record<string, string>
  /** The solved hue, saturation (0-100) and lightness (0-100) the ladder was built from. */
  hsl: { h: number; s: number; l: number }
  report: SolveStep[]
}

/** One ladder step relative to the solved ground: saturation and lightness deltas in points. */
interface StepDelta {
  ds: number
  dl: number
}

interface StepsRecipe {
  kind: 'steps'
  /** Per scheme, the deltas of surface-1, surface-2 and sunk from `bg`. */
  deltas: Record<Scheme, { surface1: StepDelta; surface2: StepDelta; sunk: StepDelta }>
}

export interface QuilHueRecipe {
  kind: 'quil-hue'
  /** The constant ink the dark ground is solved against (quil's cream). */
  ink: string
  /** The constant lit element that must separate from the dark ground (quil's gold). */
  lit: string
  /** Contrast the ink aims for, the floor it may fall back to, the lit element's minimum. */
  inkTarget: number
  inkFloor: number
  litMin: number
  /** Perceptual cap on the dark ground's lightness, in points: contrast is not darkness. */
  maxL: number
  /** Saturation is clamped into this range so a desaturated pick still gives a coloured app. An
   *  achromatic pick (`ACHROMATIC_MAX_SPREAD`) has no hue to colour it with and is held at 0 instead. */
  satRange: [number, number]
  /** The lightness the dark search starts from (downwards). */
  searchFromL: number
  /** How many 1-point darkening steps job 2 may take. */
  litGuard: number
  /** Dark: the ladder relative to the solved (h, s, l). `litChecked` steps must clear `litMin`. */
  dark: {
    surface1: StepDelta
    surface2: StepDelta
    sunk: StepDelta
    extras: Record<string, StepDelta>
    litChecked: ('bg' | 'surface1' | 'surface2')[]
  }
  /** Light: the pinned sheet, plus the pick returned as ink and as a translucent hairline. */
  light: {
    bg: string
    surface1: string
    surface2: string
    sunk: string
    /** Fixed extras, e.g. `topbar`. */
    extras: Record<string, string>
    /** Extra key and lightness of the tinted ink (quil: `text`, 14). */
    ink: { key: string; l: number }
    /** Extra key, lightness and alpha of the tinted hairline (quil: `line`, 12, .55). */
    line: { key: string; l: number; alpha: number }
    /** The `hsl.l` reported for the sheet. */
    reportedL: number
  }
  /** OLED: true black ground, the hue in the surfaces and an opaque keyline. */
  oled: {
    surface1L: number
    surface2L: number
    /** Extras pinned to true black (quil: `topbar`). */
    blackExtras: string[]
    line: { key: string; s: number; l: number }
  }
}

export type GroundRecipe = StepsRecipe | QuilHueRecipe

/** Quil's ground today (`ground.ts`, 2026-09-19 v3 "Colours"). Moves into quil's profile at adoption. */
export const QUIL_HUE_RECIPE: QuilHueRecipe = {
  kind: 'quil-hue',
  ink: '#feedd1',
  lit: '#dfa855',
  inkTarget: 10,
  inkFloor: 4.5,
  litMin: 3,
  maxL: 16,
  satRange: [12, 92],
  searchFromL: 60,
  litGuard: 20,
  dark: {
    surface1: { ds: -4, dl: 2.5 },
    surface2: { ds: -7, dl: 4.5 },
    sunk: { ds: 0, dl: -3 },
    extras: { topbar: { ds: 0, dl: -2 } },
    litChecked: ['bg', 'surface1', 'surface2'],
  },
  light: {
    bg: '#fdecd3',
    surface1: '#fde6c4',
    surface2: '#fef3e2',
    sunk: '#fcdeb1',
    extras: { topbar: '#fce2ba' },
    ink: { key: 'text', l: 14 },
    line: { key: 'line', l: 12, alpha: 0.55 },
    reportedL: 91,
  },
  oled: {
    surface1L: 4.5,
    surface2L: 7,
    blackExtras: ['topbar'],
    line: { key: 'line', s: 55, l: 42 },
  },
}

/** A default `steps` recipe: small lightness steps, surfaces toward the light, the well away from it. */
export const DEFAULT_STEPS_RECIPE: StepsRecipe = {
  kind: 'steps',
  deltas: {
    light: { surface1: { ds: 0, dl: 3 }, surface2: { ds: 0, dl: 6 }, sunk: { ds: 0, dl: -4 } },
    dark: { surface1: { ds: 0, dl: 4 }, surface2: { ds: 0, dl: 8 }, sunk: { ds: 0, dl: -3 } },
    oled: { surface1: { ds: 0, dl: 5 }, surface2: { ds: 0, dl: 9 }, sunk: { ds: 0, dl: 0 } },
  },
}

/**
 * A pick is ACHROMATIC when its largest and smallest 8-bit channels differ by at most this much
 * (2/255, under 1%). Such a colour has no hue to carry: HSL reports its hue as 0 (red) by
 * convention, and a spread of 1 or 2 is rounding noise from a picker or a hex conversion, not a
 * choice. `quil-hue` gives an achromatic pick a neutral ladder (saturation 0 in every scheme, and a
 * neutral keyline) instead of holding its saturation into `satRange` at a hue it never had, which
 * turned every grey into one red-brown (proofer 2026-11-01). A spread of 3 or more keeps its hue.
 * `steps` is unaffected: its base is the literal `bg` either way.
 */
export const ACHROMATIC_MAX_SPREAD = 2

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))
const at = (h: number, s: number, l: number): string => toHex(hslToRgb(h, s, l))

/** The darkest of (what reaches `inkTarget` against the ink) and the cap; the floor only if the
 *  target is unreachable. Quil's `solveLightness`, with its constants passed in.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function solveLightness(h: number, s: number, recipe: QuilHueRecipe): number {
  const ink: RGB = rgbOf(recipe.ink)
  for (let l = recipe.searchFromL; l >= 0; l--) {
    if (contrast(ink, hslToRgb(h, s, l)) >= recipe.inkTarget) return Math.min(l, recipe.maxL)
  }
  for (let l = recipe.searchFromL; l >= 0; l--) {
    if (contrast(ink, hslToRgb(h, s, l)) >= recipe.inkFloor) return Math.min(l, recipe.maxL)
  }
  return 0
}

function rgbOf(c: string): RGB {
  const p = mustParse(c)
  return [p[0], p[1], p[2]]
}

/** Solve a ground ladder. Never throws on a valid colour; throws naming a non-literal base. */
export function solveGround(input: GroundInput, recipe: GroundRecipe): GroundResult {
  return recipe.kind === 'steps' ? solveSteps(input, recipe) : solveQuilHue(input, recipe)
}

function solveSteps(input: GroundInput, recipe: StepsRecipe): GroundResult {
  const base = rgbOf(input.base)
  const bg = toHex(base)
  const { h, s, l } = rgbToHsl(base)
  const d = recipe.deltas[input.scheme]
  const step = (x: StepDelta): string => at(h, clamp(s + x.ds, 0, 100), clamp(l + x.dl, 0, 100))
  return {
    bg,
    surface1: step(d.surface1),
    surface2: step(d.surface2),
    sunk: step(d.sunk),
    extras: {},
    hsl: { h, s, l },
    report: [],
  }
}

function solveQuilHue(input: GroundInput, r: QuilHueRecipe): GroundResult {
  const rgb = rgbOf(input.base)
  const report: SolveStep[] = []
  // An achromatic pick has no hue: it gets saturation 0 (and hue 0, which is then inert) everywhere,
  // the keyline included. A chromatic pick takes exactly the path it always did.
  const neutral = Math.max(...rgb) - Math.min(...rgb) <= ACHROMATIC_MAX_SPREAD
  let h: number
  let s: number
  if (neutral) {
    h = 0
    s = 0
    report.push({ role: 'ground', from: input.base, to: 'neutral', why: `achromatic (channel spread <= ${ACHROMATIC_MAX_SPREAD}/255): no hue to carry` })
  } else {
    const hsl = rgbToHsl(rgb)
    h = hsl.h
    s = Math.max(r.satRange[0], Math.min(r.satRange[1], hsl.s))
    if (s !== hsl.s) report.push({ role: 'ground', from: `s ${hsl.s.toFixed(1)}`, to: `s ${s}`, why: `saturation held in ${r.satRange[0]}-${r.satRange[1]}` })
  }
  // Ladder saturation: the pick's plus a step delta, never below 0 (a neutral pick's deltas go negative).
  const sat = (ds: number): number => Math.max(0, s + ds)

  if (input.scheme === 'light') {
    const L = r.light
    return {
      bg: L.bg,
      surface1: L.surface1,
      surface2: L.surface2,
      sunk: L.sunk,
      extras: {
        ...L.extras,
        [L.ink.key]: at(h, s, L.ink.l),
        [L.line.key]: `hsl(${Math.round(h)} ${Math.round(s)}% ${L.line.l}% / ${String(L.line.alpha).replace(/^0\./, '.')})`,
      },
      hsl: { h, s, l: L.reportedL },
      report,
    }
  }

  if (input.scheme === 'oled') {
    const O = r.oled
    const extras: Record<string, string> = {}
    for (const k of O.blackExtras) extras[k] = '#000000'
    extras[O.line.key] = at(h, neutral ? 0 : O.line.s, O.line.l)
    return {
      bg: '#000000',
      sunk: '#000000',
      surface1: at(h, s, O.surface1L),
      surface2: at(h, s, O.surface2L),
      extras,
      hsl: { h, s, l: 0 },
      report,
    }
  }

  const D = r.dark
  const lit = rgbOf(r.lit)
  const solved = solveLightness(h, s, r)
  let l = solved
  const delta: Record<'bg' | 'surface1' | 'surface2', StepDelta> = { bg: { ds: 0, dl: 0 }, surface1: D.surface1, surface2: D.surface2 }
  for (let guard = 0; guard < r.litGuard; guard++) {
    let worst = Infinity
    for (const k of D.litChecked) worst = Math.min(worst, contrast(lit, hslToRgb(h, sat(delta[k].ds), l + delta[k].dl)))
    if (worst >= r.litMin || l <= 0) break
    l -= 1
  }
  if (l !== solved) report.push({ role: 'ground', from: `l ${solved}`, to: `l ${l}`, why: `darkened so ${r.lit} clears ${r.litMin}:1` })
  const step = (x: StepDelta): string => at(h, sat(x.ds), l + x.dl)
  const extras: Record<string, string> = {}
  for (const [k, x] of Object.entries(D.extras)) extras[k] = step(x)
  return {
    bg: at(h, s, l),
    surface1: step(D.surface1),
    surface2: step(D.surface2),
    sunk: step(D.sunk),
    extras,
    hsl: { h, s, l },
    report,
  }
}
